import { Check } from "lucide-react";

import { playbookCategories } from "@/data/playbooks/categories";
import { MockGenieConfig } from "@/types/mockups/genie-setup-v2";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

interface Step1JobProps {
  config: MockGenieConfig;
  onChange: (config: MockGenieConfig) => void;
}

const iconByCategory: Record<string, GenieIconName> = {
  "provide-support": "chat-check",
  "physical-world": "globe",
  "capture-leads": "mail",
  "onboard-educate": "lightbulb",
  "amplify-marketing": "eye",
  "gather-insights": "clipboard-check",
};

export const Step1Job = ({ config, onChange }: Step1JobProps) => {
  const handleSelect = (categoryId: string) => {
    onChange({ ...config, job: { categoryId } });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {playbookCategories.map((category) => {
          const isSelected = config.job.categoryId === category.id;
          const genieIcon = iconByCategory[category.id] ?? "genie-lamp";

          if (isSelected) {
            return (
              <button
                key={category.id}
                type="button"
                onClick={() => handleSelect(category.id)}
                className="text-left p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] transition-all"
              >
                <JobCardInner category={category} genieIcon={genieIcon} isSelected />
              </button>
            );
          }

          return (
            <button
              key={category.id}
              type="button"
              onClick={() => handleSelect(category.id)}
              className="text-left rounded-2xl bg-white border-2 border-gray-200 hover:border-genie-primary/40 hover:shadow-md transition-all"
            >
              <JobCardInner category={category} genieIcon={genieIcon} />
            </button>
          );
        })}
      </div>
    </div>
  );
};

const JobCardInner = ({
  category,
  genieIcon,
  isSelected,
}: {
  category: (typeof playbookCategories)[number];
  genieIcon: GenieIconName;
  isSelected?: boolean;
}) => (
  <div
    className={`relative ${isSelected ? "rounded-[14px] bg-white" : "rounded-2xl"} p-5`}
  >
    {isSelected && (
      <div className="absolute top-4 right-4 w-6 h-6 rounded-full bg-genie-primary flex items-center justify-center">
        <Check className="w-3.5 h-3.5 text-white" />
      </div>
    )}

    <div className="flex items-start gap-4">
      <GenieIcon name={genieIcon} size={40} className="shrink-0" />
      <div className="flex-1 min-w-0 pr-6">
        <h3 className="text-base font-semibold text-gray-900 mb-1">{category.name}</h3>
        <p className="text-sm text-gray-600 leading-relaxed">{category.description}</p>
      </div>
    </div>
  </div>
);
